import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import logo from "../../assets/logo.png";

import {
  FaInstagram,
  FaFacebookF,
  FaYoutube,
  FaPinterestP,
  FaTwitter,
  FaLinkedinIn,
  FaWhatsapp,
} from "react-icons/fa";
import { FiArrowRight } from "react-icons/fi";

const BASE_API = (process.env.REACT_APP_BASE_API || "").replace(/\/+$/, "");
const UPLOAD_PATH = "uploads"; // same folder your product images use

const toImageUrl = (src) => {
  if (!src) return null;
  if (/^(https?:)?\/\//.test(src) || src.startsWith("data:")) return src;
  const clean = String(src).replace(/^\/+/, "");
  return `${BASE_API}/${clean.includes("/") ? clean : `${UPLOAD_PATH}/${clean}`}`;
};

/* The admin picks a social icon by name */
const SOCIAL_ICONS = {
  instagram: FaInstagram,
  facebook: FaFacebookF,
  youtube: FaYoutube,
  pinterest: FaPinterestP,
  twitter: FaTwitter,
  linkedin: FaLinkedinIn,
  whatsapp: FaWhatsapp,
};

/* Shown until the saved content loads, and used if the API fails */
const DEFAULTS = {
  quickLinksTitle: "Quick Links",
  quickLinks: [
    { label: "Home", to: "/" },
    { label: "Shop", to: "/shop" },
    { label: "About Us", to: "/about" },
    { label: "Contact", to: "/contact-us" },
  ],
  careTitle: "Customer Care",
  careLinks: [
    { label: "Shipping", to: "#" },
    { label: "Returns & Refunds", to: "#" },
    { label: "FAQs", to: "#" },
    { label: "Track Order", to: "#" },
  ],
  followTitle: "Follow Us",
  social: [
    { platform: "instagram", href: "#" },
    { platform: "facebook", href: "#" },
    { platform: "youtube", href: "#" },
    { platform: "pinterest", href: "#" },
  ],
  newsletterTitle: "Join our wellness family",
  copyright: "Jeetayu. All rights reserved. Powered by Aadviktech.",
  tagline: "Ayurveda for a better life",
};

const linkCls = "text-[13px] text-[#cfe0cb] hover:text-white transition-colors";

function FooterLinks({ title, links }) {
  return (
    <div>
      <h3 className="text-[14px] font-semibold text-white mb-4">{title}</h3>
      <ul className="flex flex-col gap-3">
        {links.map((l, i) => (
          <li key={`${l.to}-${i}`}>
            {/^(https?:)?\/\//.test(l.to) ? (
              <a href={l.to} target="_blank" rel="noreferrer" className={linkCls}>
                {l.label}
              </a>
            ) : (
              <Link to={l.to} className={linkCls}>
                {l.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* Pass `onSubscribe={async (email) => { await api.post("/subscribe", { email }) }}`
   from where you render <Footer />. Without it, signup shows a "not connected" message. */
function Footer({ onSubscribe }) {
  const [c, setC] = useState(DEFAULTS);
  const [logoSrc, setLogoSrc] = useState(logo);
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState(null); // {type, text}
  const [loading, setLoading] = useState(false);

  /* Load the footer content saved from the admin panel */
  useEffect(() => {
    const controller = new AbortController();
    fetch(`${BASE_API}/footerinfo`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => {
        setC({ ...DEFAULTS, ...d });
        if (d.logo) setLogoSrc(toImageUrl(d.logo));
      })
      .catch(() => {}); // keep the defaults
    return () => controller.abort();
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setMsg({ type: "error", text: "Please enter a valid email address." });
      return;
    }
    if (!onSubscribe) {
      setMsg({ type: "error", text: "Newsletter signup is not connected yet." });
      return;
    }
    try {
      setLoading(true);
      await onSubscribe(email.trim());
      setMsg({ type: "success", text: "Thanks for joining our wellness family!" });
      setEmail("");
    } catch (err) {
      setMsg({
        type: "error",
        text: err?.response?.data?.message || "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="w-full bg-gradient-to-br from-[#1f4a2b] to-[#143520] text-white">
      <div className="w-full max-w-[1100px] mx-auto px-4 pt-12 sm:pt-14 pb-8">
        <div className="grid grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1fr_1.5fr] gap-x-6 gap-y-10 lg:gap-x-8">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-1">
            <Link to="/" className="inline-flex items-center gap-3">
              <img
                src={logoSrc}
                alt=""
                className="w-[100px]"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = logo;
                }}
              />
            </Link>
          </div>

          <FooterLinks title={c.quickLinksTitle} links={c.quickLinks} />
          <FooterLinks title={c.careTitle} links={c.careLinks} />

          {/* Follow us */}
          <div>
            <h3 className="text-[14px] font-semibold text-white mb-4">{c.followTitle}</h3>
            <div className="flex items-center gap-3">
              {c.social.map((s, i) => {
                const Icon = SOCIAL_ICONS[s.platform] || FaInstagram;
                return (
                  <a
                    key={`${s.platform}-${i}`}
                    href={s.href}
                    aria-label={s.platform}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#cfe0cb] hover:text-white transition-colors"
                  >
                    <Icon className="text-[18px]" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Join */}
          <div className="col-span-2 lg:col-span-1">
            <h3 className="text-[14px] font-semibold text-white mb-4">{c.newsletterTitle}</h3>
            <form onSubmit={handleSubscribe} noValidate>
              <div className="flex items-center bg-white rounded-md overflow-hidden">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setMsg(null);
                  }}
                  placeholder="Enter your email"
                  aria-label="Email address"
                  className="flex-1 min-w-0 h-[40px] px-3 text-[13px] text-[#1f2d22] placeholder:text-[#9aa79c] focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={loading}
                  aria-label="Subscribe"
                  className="w-[44px] h-[40px] flex items-center justify-center bg-[#2f6b3a] hover:bg-[#3b8348] disabled:opacity-60 transition-colors text-white"
                >
                  <FiArrowRight className="text-[16px]" />
                </button>
              </div>
              {msg && (
                <p
                  className={`mt-2 text-[12px] ${
                    msg.type === "success" ? "text-[#b7e3a8]" : "text-[#ffb4b4]"
                  }`}
                >
                  {msg.text}
                </p>
              )}
            </form>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-5 border-t border-white/15 flex flex-col sm:flex-row items-center justify-between gap-2 text-[12px] text-[#cfe0cb]">
          <p className="text-center sm:text-left">
            © {new Date().getFullYear()} {c.copyright}
          </p>
          <p>{c.tagline}</p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;